import {
  EntityId,
  PrinticularProductTemplateVariant,
  PrintServiceProductEntity,
  PrintServiceProductImageEntity,
} from "@jackfruit/common"
import { SagaIterator } from "@redux-saga/types"
import { PayloadAction } from "@reduxjs/toolkit"
import { call, put, select, take, takeEvery } from "redux-saga/effects"
import {
  getImageCountPerPageSpreadForProduct,
  getMinAndMaxImagesForPhotoBookProduct,
} from "~/components/editors/photo-book/PhotoBookUtils"
import { ImageRegionEntity } from "~/interfaces/entities/ImageRegion"
import { LineItemEntity } from "~/interfaces/entities/LineItem"
import { PageType, ProductPageEntity } from "~/interfaces/entities/ProductPage"
import { actions, UpdateMissingImagesForLineItem } from "../process"
import { printServiceProductImagesSelector } from "../state/printServiceProductImages"
import { printServiceProductsSelector } from "../state/printServiceProducts"
import { RootState } from "../store"
import { getAllImageRegionsForLineItem } from "./imageRegion"
import { getLineItem } from "./lineItems"
import { insertPageSpreadToLineItem } from "./processLineItems"
import { getProductPage, getProductPages } from "./productPages"
import { getProductTemplate } from "./productTemplates"

export default function* watchUpdateMissingImagesForLineItem() {
  yield takeEvery(
    actions.updateMissingImagesForLineItem.type,
    processUpdateMissingImagesForLineItem
  )
}

function* processUpdateMissingImagesForLineItem(
  action: PayloadAction<UpdateMissingImagesForLineItem>
): SagaIterator<any> {
  const { lineItemId, files, imageRegionId, onOpen, onClose } = action.payload

  const lineItem: LineItemEntity = yield call(getLineItem, lineItemId)

  let templateVariant: PrinticularProductTemplateVariant | undefined
  if (lineItem.productTemplateId && lineItem.productTemplateVariantId) {
    const productTemplate: { variants: PrinticularProductTemplateVariant[] } =
      yield call(getProductTemplate, lineItem.productTemplateId)

    templateVariant = productTemplate.variants.find(
      variant => variant.key === lineItem.productTemplateVariantId
    )
  }

  const productPages: ProductPageEntity[] = yield call(
    getProductPages,
    lineItem.productPageIds
  )
  const product: PrintServiceProductEntity = yield select((state: RootState) =>
    printServiceProductsSelector.selectById(state, lineItem.productId)
  )

  const isPhotoBook = product.categoryName === "photo-book"

  if (isPhotoBook) {
    yield call(uploadMissingImagesForPhotoBook, {
      lineItemId,
      imageRegionId,
      files,
      product,
      productPages,
      templateVariant,
      onOpen,
      onClose,
    })
  } else {
    yield call(uploadMissingImagesForNormalProducts, {
      lineItemId,
      imageRegionId,
      files,
      product,
      onOpen,
      onClose,
    })
  }
}

function* uploadMissingImagesForNormalProducts({
  lineItemId,
  imageRegionId,
  files,
  product,
  onOpen,
  onClose,
}: {
  lineItemId: EntityId
  imageRegionId?: EntityId
  files: File[]
  product: PrintServiceProductEntity
  onOpen?: () => void
  onClose?: () => void
}) {
  const isClampingEnabled = Boolean(
    product?.metaData?.web?.editor?.clamp ?? true
  )
  const imageFilterName = product?.metaData?.defaultImageFilter

  const allImageRegionsForLineItem: ImageRegionEntity[] = yield call(
    getAllImageRegionsForLineItem,
    lineItemId
  )

  let allImageRegions = allImageRegionsForLineItem
  if (imageRegionId) {
    const imageRegionIndex = allImageRegionsForLineItem.findIndex(
      imageRegion => imageRegion.id === imageRegionId
    )

    allImageRegions = allImageRegionsForLineItem.slice(imageRegionIndex)
  }

  const imageRegionCount = allImageRegions.length
  const imageRegionWithImageCount = allImageRegions.filter(imageRegion =>
    Boolean(imageRegion.uploadId)
  ).length

  // images has already been applied to line item
  // but some pages are still empty
  const requireConfirmation =
    imageRegionWithImageCount > 0 &&
    imageRegionWithImageCount < imageRegionCount &&
    !imageRegionId

  let confirm = true
  if (requireConfirmation) {
    onOpen?.()
    const {
      payload: { choice },
    } = yield take(actions.setUpdateMissingImagesDialogChoice.type)
    onClose?.()
    confirm = choice
  }

  // we got the user confirmation, let’s apply new images to empty pages
  if (confirm) {
    let currentFileIndex = 0
    for (let i = 0; i < allImageRegions.length; i++) {
      const currentImageRegion = allImageRegions[i]
      const hasUploadAttached = Boolean(currentImageRegion.uploadId)
      if (!hasUploadAttached) {
        const currentFile = files[currentFileIndex]
        yield put(
          actions.updateImageRegionImage({
            imageRegionId: currentImageRegion.id,
            file: currentFile,
            isClampingEnabled,
            imageFilterName,
          })
        )
        currentFileIndex++
        // no more files available, let’s break out
        if (currentFileIndex >= files.length) {
          break
        }
      }
    }
  }
}

//Photo book uploading image behaviour
function* uploadMissingImagesForPhotoBook({
  lineItemId,
  imageRegionId,
  files,
  product,
  productPages,
  templateVariant,
  onOpen,
  onClose,
}: {
  lineItemId: EntityId
  imageRegionId?: EntityId
  files: File[]
  product: PrintServiceProductEntity
  productPages: ProductPageEntity[]
  templateVariant?: PrinticularProductTemplateVariant
  onOpen?: (type?: string) => void
  onClose?: () => void
}) {
  const printServiceProductImages: PrintServiceProductImageEntity[] =
    yield select((state: RootState) =>
      printServiceProductImagesSelector.selectByIds(
        state,
        product.printServiceProductImages
      )
    )
  const { maxPages, maxImages } = getMinAndMaxImagesForPhotoBookProduct({
    ...product,
    printServiceProductImages,
  })

  const allImageRegionsForLineItem: ImageRegionEntity[] = yield call(
    getAllImageRegionsForLineItem,
    lineItemId
  )

  let imageRegions = allImageRegionsForLineItem
  if (imageRegionId) {
    const imageRegionIndex = allImageRegionsForLineItem.findIndex(
      imageRegion => imageRegion.id === imageRegionId
    )

    imageRegions = allImageRegionsForLineItem.slice(imageRegionIndex)
  }

  const filesCount = files.length
  const imageRegionsCount = imageRegions.length
  const uploadedImagesCount = imageRegions.filter(imageRegion =>
    Boolean(imageRegion.uploadId)
  ).length

  const isClampingEnabled = Boolean(
    product?.metaData?.web?.editor?.clamp ?? true
  )
  const imageFilterName = product?.metaData?.defaultImageFilter
  const isSimplex = product?.metaData?.photoBook?.bookType === "simplex"

  //image regions to add equals
  //number of uploading images + number of uploaded images - number of existing image regions
  const imageRegionAmountToAdd =
    filesCount + uploadedImagesCount - imageRegionsCount
  const imageRegionMaxAmountToAdd = maxImages - imageRegionsCount

  // images has already been applied to line item
  // but some pages are still empty
  const showExceedMaxImagesWarning =
    imageRegionAmountToAdd > imageRegionMaxAmountToAdd
  const requireConfirmation =
    uploadedImagesCount > 0 &&
    uploadedImagesCount < imageRegionsCount &&
    !imageRegionId

  let confirm = true
  if (showExceedMaxImagesWarning && onOpen) {
    onOpen?.("warning")
    const {
      payload: { choice },
    } = yield take(actions.setUpdateMissingImagesDialogChoice.type)
    onClose?.()
    confirm = choice
  }

  if (requireConfirmation && !showExceedMaxImagesWarning && onOpen) {
    onOpen?.()
    const {
      payload: { choice },
    } = yield take(actions.setUpdateMissingImagesDialogChoice.type)
    onClose?.()
    confirm = choice
  }

  // we got the user confirmation, let's apply new images to empty pages
  if (confirm) {
    const pageSpreadInsertPosition = productPages.length - 2
    const imageCountPerPageSpread = getImageCountPerPageSpreadForProduct(
      {
        ...product,
        printServiceProductImages,
      },
      templateVariant,
      pageSpreadInsertPosition - 1 // minus 1 to offset the last page at the end
    )

    const currentPageCount = productPages.filter(
      page => page.pageType === PageType.Page
    ).length
    const maxPageSpreadToAdd = Math.ceil((maxPages - currentPageCount) / 2)
    const pageSpreadToAddIdeally =
      imageRegionAmountToAdd / imageCountPerPageSpread

    const pageSpreadToAdd = Math.min(pageSpreadToAddIdeally, maxPageSpreadToAdd)

    const newImageRegionIds: EntityId[] = []
    for (
      let pageSpreadIndex = 0;
      pageSpreadIndex < Math.ceil(pageSpreadToAdd);
      pageSpreadIndex++
    ) {
      const positionOffset = isSimplex
        ? pageSpreadIndex * 2
        : pageSpreadIndex * 2 + 1

      const newPageIds: EntityId[] = yield call(() =>
        insertPageSpreadToLineItem(
          lineItemId,
          pageSpreadInsertPosition + positionOffset
        )
      )

      const leftPage: ProductPageEntity = yield call(
        getProductPage,
        newPageIds[0]
      )
      const rightPage: ProductPageEntity = yield call(
        getProductPage,
        newPageIds[1]
      )

      const leftPageImageRegionIds = leftPage.imageRegionIds
      const rightPageImageRegionIds = rightPage.imageRegionIds

      const batchOfImageRegionIds = [
        ...leftPageImageRegionIds,
        ...rightPageImageRegionIds,
      ]

      newImageRegionIds.push(...batchOfImageRegionIds)
    }

    const allImageRegionsIncludingNewPageSpreads: ImageRegionEntity[] =
      yield call(getAllImageRegionsForLineItem, lineItemId)

    let allImageRegions = allImageRegionsIncludingNewPageSpreads
    if (imageRegionId) {
      const imageRegionIndex = allImageRegionsIncludingNewPageSpreads.findIndex(
        imageRegion => imageRegion.id === imageRegionId
      )

      allImageRegions =
        allImageRegionsIncludingNewPageSpreads.slice(imageRegionIndex)
    }

    const allImageRegionCount = allImageRegions.length

    let currentFileIndex = 0
    for (let i = 0; i < allImageRegionCount; i++) {
      const currentImageRegion = allImageRegions[i]
      const hasUploadAttached = Boolean(currentImageRegion.uploadId)
      if (!hasUploadAttached) {
        const currentFile = files[currentFileIndex]
        yield put(
          actions.updateImageRegionImage({
            imageRegionId: currentImageRegion.id,
            file: currentFile,
            isClampingEnabled,
            imageFilterName,
          })
        )

        currentFileIndex++
        if (currentFileIndex >= filesCount) {
          break
        }
      }
    }
  }
}
